<template>
  <TableCell v-bind="$attrs"
    :tag="tag"
    :popoverPadding="'0px'"
    :showingPopover="showingPopover"
    @update:showingPopover="value => $emit('update:showingPopover', value)"
  >
    <template v-if="committed">
      {{ tag.value }}
    </template>

    <template #BinSelectionInput>
      <slot name="BinSelectionInput"></slot>
    </template>
    
    <template #PopoverContentDesktop>
      <TieredMenu ref="menu" :model="menuItems">
        <template #start>
          <div style="padding: 15px; display: flex; align-items: center; gap: 15px;">
            <h3>
              Operation mode
            </h3>
            <div style="flex-grow: 1;"></div>
            <Button text severity="secondary" style="color: gray; border: 0; margin-right: -10px;" @click="$emit('update:showingPopover', false)">
              <i class="pi pi-times"></i>
            </Button>
          </div>
        </template>
      </TieredMenu>
    </template>
  </TableCell>

  <Dialog modal dismissableMask position="top" v-model:visible="showingModeTimes" :draggable="false" :closable="false" style="min-width: 600px;"
    :pt="{ content: { class: 'operation-mode-times-dialog-content' } }"
  >
    <template #header>
      <div style="flex-grow: 1;">
        <div style="flex-grow: 1; display: flex; align-items: center;">
          <h3 style="flex-grow: 1;">
            Accumulated times
          </h3>
          <Button text severity="secondary" style="border: 0; color: gray;" @click="showingModeTimes = false">
            <i class="pi pi-times"></i>
          </Button>
        </div>
        <div style="display: flex; align-items: center; gap: 10px;">
          Show accumulated times for previous
          <Dropdown v-model="modeTimesDuration" optionLabel="text" optionValue="value"
            :options="modeTimesDurationOptions"
            :pt="{ wrapper: { style: { maxHeight: 'initial' } } }"
          ></Dropdown>
        </div>
      </div>
    </template>
    <MultiColumnTable :tableData="modeTimesTableData" :style="{ opacity: modeTimesPending ? 0.5 : 1 }"></MultiColumnTable>
  </Dialog>

</template>

<script>
import { reactive } from "vue";
import { createMultiSystemTable } from "./TableUtility.js";
import MultiColumnTable from "./MultiColumnTable.vue";
import TieredMenu from "primevue/tieredmenu";
import TableCell from "./TableCell.vue";
import router from "./router.js";
import store from "./store.js";

export let modeTimesBySessionId = reactive({ value: {} });

export default {
  components: { TableCell, TieredMenu, MultiColumnTable },
  props: [
    "tag",
    "showingPopover"
  ],
  data() {
    return { 
      store,
      committed: true,
      showingModeTimes: false,
      modeTimesPending: true,
      modeTimesDuration: "today",
      modeTimesDurationOptions: [
        { text: "1 Day", value: "today" },
        { text: "2 days", value: "2days" },
        { text: "3 days", value: "3days" },
        { text: "4 days", value: "4days" },
        { text: "5 days", value: "5days" },
        { text: "6 days", value: "6days" },
        { text: "1 week", value: "1week" },
        { text: "2 weeks", value: "2weeks" },
        { text: "3 weeks", value: "3weeks" },
        { text: "4 weeks", value: "4weeks" }
      ]
    };
  },
  computed: {
    bin() {
      let system = this.tag.$root();
      let bins = system.bins.children;
      return this.tag.$parents().find(parent => bins.includes(parent));
    },
    menuItems() {
      return [
        { separator: true },
        {
          label: this.bin.enabled.value ? "Disable bin" : "Enable bin",
          command: () => this.toggleBinEnabled()
        },
        { 
          label: "Press start button",
          visible: this.bin.enabled.value && this.bin.showVirtualStartButton.value,
          command: () => this.setVirtualStartButtonPressed()
        },
        {
          label: "Press stop button",
          visible: this.bin.enabled.value && !this.bin.showVirtualStartButton.value,
          command: () => this.setVirtualStopButtonPressed()
        },
        { label: "Mode schedules", command: () => router.push("/modes") },
        { label: "Accumulated mode times", command: () => {
          this.showingModeTimes = true;
          this.$emit("update:showingPopover", false);
        }},
      ];
    },
    modeTimesTableData() {
      let table = createMultiSystemTable("accumulatedModeTimes");
      let getTime = (cell) => {
        let bin = cell.tag;
        let binNumber = bin.$key();
        let modeIndex = cell.componentArgs.modeIndex;
        let whichTime = cell.componentArgs.whichTime;
        let sessionId = cell.tag.$session.sessionId;
        let modeTimes = modeTimesBySessionId.value[sessionId];
        if (modeTimes == null) return 0;
        if (whichTime == "modesTotal") return 1;
        if (whichTime == "fansTotal") return 1;
        let value = modeTimes[binNumber][modeIndex][whichTime];
        return value;
      };
      table.rows = table.rows.filter(row => {
        return row.cells.some(cell => getTime(cell) > 0);
      })
      table.rows.forEach(row => {
        row.cells.forEach(cell => {
          if (getTime(cell) == 0)
            cell.componentType = "PlaceholderCell";
        });
      });
      return table;
    }
  },
  methods: {
    setVirtualStartButtonPressed() {
      this.committed = false;
      this.$emit("update:showingPopover", false);
      this.tag.$session.queueRequest("/cmd/setVirtualStartButtonPressed", { binNumber: this.bin.$key() }, () => {
        this.committed = true;
      });
    },
    setVirtualStopButtonPressed() {
      this.committed = false;
      this.$emit("update:showingPopover", false);
      this.tag.$session.queueRequest("/cmd/setVirtualStopButtonPressed", { binNumber: this.bin.$key() }, () => {
        this.committed = true;
      });
    },
    toggleBinEnabled() {
      this.committed = false;
      this.$emit("update:showingPopover", false);
      this.tag.$session.changeSettings([{
        handle: this.bin.enabled.handle,
        value: !this.bin.enabled.value
      }], () => {
        this.committed = true;
      });
    }
  },
  watch: {
    showingModeTimes() {
      store.sessions.forEach(session => {
        if (this.showingModeTimes) {
          session.startPollingFor("modeTimes", { duration: this.modeTimesDuration }, response => {
            if (response.duration != this.modeTimesDuration) {
              return;
            }
            else {
              this.modeTimesPending = false;
              modeTimesBySessionId.value[session.sessionId] = response.bins;
            }
          });
        }
        else {
          session.stopPollingFor("modeTimes");
        }
      });
    },
    modeTimesDuration() {
      this.modeTimesPending = true;
      store.sessions.forEach(session => {
        session.startPollingFor("modeTimes", { duration: this.modeTimesDuration }, response => {
          if (response.duration != this.modeTimesDuration) {
            return;
          }
          else {
            this.modeTimesPending = false;
            modeTimesBySessionId.value[session.sessionId] = response.bins;
          }
        });
      });
    }
  }
}
</script>

<style>
.operation-mode-status-popover[data-popper-placement^='bottom'] > .arrow::before {
  background: #e6e6e6;
}
.operation-mode-times-dialog-content {
  display: flex;
  justify-content: center;
  padding-top: 0;
}
</style>

<style scoped>
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  overflow: auto;
  z-index: 20;

  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 25px;
}
.dialog {
  position: relative;
  background: white;
  border: 0px solid rgb(55,55,55);
  border-radius: 5px;
  padding: 20px;
  /* font-size: large; */
  min-width: 600px;
  box-shadow: 0px 46px 47px -26px rgba(0,0,0,0.75);
}
.dialog-title {
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
}
.dialog-title h3 {
  font-weight: bold;
}
.operation-mode-status-popover button {
  height: 35px;
  padding: 8px;
  cursor: pointer;
  padding-left: 12px;
  padding-right: 12px;
  color: black;
  background: white;
  border-radius: 3px;
  border: 1px solid rgb(196, 196, 196);
  /* box-shadow: 0 0 2px rgb(0 0 0 / 7%), 0 1px 2px rgb(0 0 0 / 15%); */
}
</style>
